import { call, put, select, takeLatest } from 'redux-saga/effects';

import { arrayEquals } from '@nhl/generic-collection-utils';

import { statsConfigSelectors } from '../../config';
import { dataTableActionCreators, dataTableSelectors } from '../dataTable';
import { filterActionCreators, filterSelectors, filterDefaults } from '../filter';
import { reportActionCreators, reportActionTypes, reportSelectors } from '../report';

// INDIVIDUAL SAGAS
/* validateOpponentUsingPlayerFranchise - reset "opponent" if current "opponent" is the same as the selected franchiseId
 * @modifies {state|playersAndTeams|filter|values|opponent}
 */
export function* validateOpponentUsingPlayerFranchise() {
    const { franchiseId } = yield select(reportSelectors.getCurrentReport);
    const { opponent } = yield select(filterSelectors.getFilterValues);

    // Early return if opponent nor franchiseId does not exist
    if (!opponent) {
        return;
    }

    if (franchiseId === opponent) {
        yield put(filterActionCreators.resetFilter('opponent'));
    }
}

/* validateDecision - reset "decision" if not available for time period
 * @modifies {state|playersAndTeams|filter|values|decision}
 */
export function* validateDecision() {
    const { decision } = yield select(filterSelectors.getFilterValues);

    if (!decision) {
        return;
    }

    const config = yield select(statsConfigSelectors.getConfig);
    const report = yield select(reportSelectors.getCurrentReport);
    const decisions = statsConfigSelectors.getListOfThingsForReport(config, report, 'gameResults');

    if (!decisions.includes(decision)) {
        yield put(filterActionCreators.resetFilter('decision'));
    }
}

/* validateCustomFilters - remove any filters regarding measures that are not in the report
 * @modifies {state|playersAndTeams|filter|values|customFilters}
 */
export function* validateCustomFilters() {
    const { customFilters } = yield select(filterSelectors.getFilterValues);
    const report = yield select(reportSelectors.getCurrentReport);
    const config = yield select(statsConfigSelectors.getConfig);

    const reportData = statsConfigSelectors.getTimeSpecificDataOnReport(config, report);
    const validMeasures = reportData.resultFilters;

    const validCustomFilters = customFilters.filter((filter) => validMeasures.includes(filter.measure));

    if (!validCustomFilters.length) {
        yield put(filterActionCreators.setCustomFilters(filterDefaults.values.customFilters));
    } else if (validCustomFilters.length !== customFilters.length) {
        yield put(filterActionCreators.setCustomFilters(validCustomFilters));
    }
}

export function* setToDefaultSort() {
    const sort = yield select(dataTableSelectors.getSort);
    const report = yield select(reportSelectors.getCurrentReport);
    const config = yield select(statsConfigSelectors.getConfig);

    const reportData = statsConfigSelectors.getTimeSpecificDataOnReport(config, report);
    const defaultSort = dataTableSelectors.getDefaultSort(report, reportData);

    const areSortsEqual =
        sort.length === defaultSort.length &&
        sort.reduce((bool, obj, index) => {
            const defaultObj = defaultSort[index];
            if (obj.id !== defaultObj.id || obj.desc !== defaultObj.desc) {
                return false;
            }
            return bool;
        }, true);

    if (!areSortsEqual) {
        yield put(dataTableActionCreators.resetSort(defaultSort));
    }
}

export function* validateSort() {
    const sort = yield select(dataTableSelectors.getSort);
    const currentReport = yield select(reportSelectors.getCurrentReport);
    const appliedReport = yield select(reportSelectors.getAppliedReport);
    const config = yield select(statsConfigSelectors.getConfig);

    const reportData = statsConfigSelectors.getTimeSpecificDataOnReport(config, currentReport);
    const invalidSort = sort.some((col) => !reportData.displayItems.includes(col.id));
    const reportChanged = currentReport.reportName !== appliedReport.reportName;
    const aggColInvalidSort = sort.some((e) => config.aggregatedColumns.includes(e.id));
    const indColInvalidSort = sort.some((e) => config.individualColumns.includes(e.id));
    if (invalidSort || reportChanged || aggColInvalidSort || indColInvalidSort) {
        const defaultSort = dataTableSelectors.getDefaultSort(currentReport, reportData);
        yield put(dataTableActionCreators.resetSort(defaultSort));
    }
}

export const areObjectsDifferent = (object1, object2) => {
    if (Object.keys(object1).length !== Object.keys(object2).length) {
        return true;
    }
    return Object.keys(object1).some((key) => {
        const value1 = object1[key];
        const value2 = object2[key];
        if (key === 'players') {
            return !arrayEquals(value1, value2);
        }
        return value1 !== value2;
    });
};

function* applyReport() {
    const { values, applied } = yield select(filterSelectors.getFilterState);
    const currentReport = yield select(reportSelectors.getCurrentReport);
    const appliedReport = yield select(reportSelectors.getAppliedReport);

    const isAppliedFiltersDiffThanCurrentFilters = areObjectsDifferent(values, applied);
    const isAppliedReportDiffThanCurrentReport = areObjectsDifferent(currentReport, appliedReport);

    if (isAppliedFiltersDiffThanCurrentFilters || isAppliedReportDiffThanCurrentReport) {
        // Rather than two separate calls to setAppliedFilters and setAppliedReport which both trigger a data re-fetch
        // we dispatch one action to update state, and only one api call is made
        yield put(reportActionCreators.setReportAndFilters(currentReport, values));
    }
}

// GROUPING SAGAS
export function* validateOpponent() {
    yield call(validateOpponentUsingPlayerFranchise);
}

export function* validateFields() {
    yield call(validateOpponent);
    yield call(validateDecision);
    yield call(validateCustomFilters);
}

// Clicking "RunReport" should fetch new data, but we need to validate the current filter and sort states first
export function* prepToRequestData() {
    yield call(validateFields);
    yield call(validateSort);
    yield put(dataTableActionCreators.resetPagination());

    // Once everything is validated apply the report which will trigger a fetch
    yield call(applyReport);
}

// EXPORT
export default function* watchForNewReport() {
    yield takeLatest(reportActionTypes.RUN_REPORT, prepToRequestData);
}
